import React, { useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

interface Step {
  key: string;
  label: string;
}

interface ModalStepperProps {
  steps: Step[];
  currentStep: string;
}

/**
 * Continuous-track stepper. Each circle is centered over its own label, and the
 * labels sit flush with the modal content edges (first flush-left under the
 * title, last flush-right under the close button), so longer labels in another
 * locale just spread inward. The track is measured to connect the real first and
 * last circle centers, whatever those labels' widths turn out to be.
 *
 * Middle nodes are spaced by index (`i / (n - 1)`), not a hard-coded 50%. That
 * keeps create-job (3 steps → single middle at 50%) identical while making
 * edit-job (4+ steps) render every circle instead of stacking them.
 */
export const ModalStepper: React.FC<ModalStepperProps> = ({ steps, currentStep }) => {
  const stepCount = steps.length;
  const currentIdx = Math.max(0, steps.findIndex((s) => s.key === currentStep));
  const progress = stepCount > 1 ? currentIdx / (stepCount - 1) : 0;

  const containerRef = useRef<HTMLDivElement>(null);
  const firstCircleRef = useRef<HTMLDivElement>(null);
  const lastCircleRef = useRef<HTMLDivElement>(null);
  const [track, setTrack] = useState({ left: 16, right: 16 });

  useLayoutEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      const first = firstCircleRef.current;
      const last = lastCircleRef.current;
      if (!container || !first || !last) return;
      const c = container.getBoundingClientRect();
      const f = first.getBoundingClientRect();
      const l = last.getBoundingClientRect();
      const next = {
        left: Math.round(f.left + f.width / 2 - c.left),
        right: Math.round(c.right - (l.left + l.width / 2)),
      };
      // Only commit real changes — measuring after our own track update would
      // otherwise loop forever.
      setTrack((prev) =>
        prev.left === next.left && prev.right === next.right ? prev : next,
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [stepCount, currentStep]);

  if (stepCount === 0) return null;

  return (
    <div ref={containerRef} className="relative h-14">
      {/* Track */}
      <div
        className="absolute top-4 h-px bg-gray-200"
        style={{ left: track.left, right: track.right }}
      />
      <motion.div
        className="absolute top-4 h-px bg-purple origin-left"
        style={{ left: track.left, right: track.right }}
        initial={false}
        animate={{ scaleX: progress }}
        transition={{ type: 'spring', stiffness: 180, damping: 28 }}
      />

      {/* Step nodes */}
      {steps.map((step, i) => {
        const isActive = i === currentIdx;
        const isPast = i < currentIdx;
        const isFirst = i === 0;
        const isLast = i === stepCount - 1;

        // First / last flush to modal content edges. Every other node at
        // i/(n-1) of the container width (translateX -50% so the circle sits
        // on that point). For n=3 that is still 50%; for n=4 → 33% / 66%.
        const positionStyle: React.CSSProperties = isFirst
          ? { left: 0 }
          : isLast
            ? { right: 0 }
            : {
                left: `${(i / (stepCount - 1)) * 100}%`,
                transform: 'translateX(-50%)',
              };

        return (
          <div
            key={step.key}
            className="absolute top-0 flex flex-col items-center gap-2"
            style={positionStyle}
          >
            <motion.div
              ref={isFirst ? firstCircleRef : isLast ? lastCircleRef : undefined}
              initial={false}
              animate={{ scale: isActive ? 1.05 : 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              className={[
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium font-heading',
                'border transition-colors',
                // Every state must be fully opaque so the track behind doesn't bleed through.
                // 6px white halo around each circle masks the violet track
                // so there's a visible gap between the track and each circle.
                isActive
                  ? 'bg-purple border-purple text-dark-invirtus shadow-[0_0_0_6px_#fff]'
                  : isPast
                    ? 'bg-white border-purple text-purple shadow-[0_0_0_6px_#fff]'
                    : 'bg-white border-gray-300 text-gray-400 shadow-[0_0_0_6px_#fff]',
              ].join(' ')}
            >
              {isPast ? <Check className="h-4 w-4" strokeWidth={2.5} /> : i + 1}
            </motion.div>
            <span
              className={[
                'text-[11px] sm:text-xs font-medium font-heading uppercase tracking-wide whitespace-nowrap transition-colors',
                isActive || isPast ? 'text-dark-invirtus' : 'text-gray-400',
              ].join(' ')}
            >
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};
